import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ComponentBaseComponent } from '@app/core/components/component-base/component-base.component';
import { FormButton } from '@app/core/components/form-field-button/form-field-button.component';
import { Item } from '@app/core/components/listbox/listbox.component';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { ActivityTypeItem, EconomyService } from '@app/services/economy.service';
import { BusinesscoItem, LogisticService } from '@app/services/logistic.service';
import { ItemCustact, TypeaheadService } from '@app/services/typeahead.service';
import { EmailArchivedEvent, EmailService, OrderSearchItem, Project, PurchSearchItem, EnrichedMessage } from '@app/services/email.service';
import { PopupService } from '@app/core/services/popup.service';
import { StateService } from '@app/core/services/state.service';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { Grid } from '@app/core/components/grid/grid.model';
import { ProjectProcessItem, ProjectProcessService } from '@app/services/project-process.service';

@Component({
    selector: 'tt-email-archive-form',
    templateUrl: './email-archive-form.component.html',
    styleUrls: ['./email-archive-form.component.css'],
})
export class EmailArchiveFormComponent extends ComponentBaseComponent implements OnInit, OnChanges {
    // #region props

    /**
     * @default false
     */
    @Input()
    public set showCreateVoucher(value: BooleanInput) {
        this._showCreateVoucher = coerceBooleanProperty(value);
    }
    public get showCreateVoucher(): boolean {
        return this._showCreateVoucher;
    }
    private _showCreateVoucher: boolean = false;

    @Input()
    public archiveHeading: string = '';

    @Input()
    public email?: EnrichedMessage;

    @Output()
    public emailArchived = new EventEmitter<EmailArchivedEvent>();

    // #endregion props

    // #region form-buttons

    public fromCustomerFormButtons: FormButton[] = [{ id: 'goto', type: 'primary', icon: 'fas fa-chevron-right', goto: 'customersmenu', gotoParms: () => ({ cust_no: '' + this.email!.from_cust_no }) }];

    public fromCustomerEmptyFormButtons: FormButton[] = [{ id: 'create', type: 'primary', icon: 'fas fa-plus', onClick: () => this.openCustomerEditPopup() }];

    public customerButtons: FormButton[] = [{ id: 'goto', type: 'primary', icon: 'fas fa-chevron-right', goto: 'customersmenu', gotoParms: () => ({ cust_no: '' + this.email!.cust_no }) }];

    public projectButtons: FormButton[] = [{ id: 'goto', type: 'primary', icon: 'fas fa-chevron-right', goto: 'projectmenu', gotoParms: () => ({ project_keyno: '' + this.email!.project_keyno }) }];

    public emptyProjectButtons: FormButton[] = [{ id: 'all', type: 'primary', icon: 'fas fa-bars', goto: 'projects', gotoParms: () => ({ argtype: '-', argvalue: '-' }) }];

    public custactButtons: FormButton[] = [{ id: 'goto', type: 'primary', icon: 'fas fa-chevron-right', goto: 'workdetails', gotoParms: () => ({ custact_no: '' + this.email!.custact_no }) }];

    public orderFormButton: FormButton[] = [{ id: 'goto', type: 'primary', icon: 'fas fa-chevron-right', goto: 'orderhead', gotoParms: () => ({ order_internal_no: '' + this.email!.order_internal_no }) }];

    public purchaseFormButton: FormButton[] = [{ id: 'goto', type: 'primary', icon: 'fas fa-chevron-right', goto: 'purchhead', gotoParms: () => ({ purch_internal_no: '' + this.email!.purch_internal_no }) }];

    // #endregion form-buttons

    public fromPersons: Item[] = [];

    public projectProcesses: ProjectProcessItem[] = [];

    public activityTypes: ActivityTypeItem[] = [];

    public custactMembers: Item[] = [];

    public companies: BusinesscoItem[] = [];

    public portalUsers: { [key: string]: any }[] = [];

    public saving = false;

    private async loadPortalUsers() {
        this.portalUsers = await this.datatask.Post(3870, { businessco_no: this.email?.businessco_no });
    }

    public async onParticipantSelected() {
        if (!this.email) return;

        this.email.participant_pu_keynos = this.portalUsers
            .filter((item) => item['is_selected'] === true)
            .reduce((acc, item) => `${acc}${item['portal_user_keyno']}, `, '')
            .slice(0, -2);
        this.email.participant_pu_names = this.portalUsers
            .filter((item) => item['is_selected'] === true)
            .reduce((acc, item) => `${acc}${item['user_name']}, `, '')
            .slice(0, -2);
    }

    public grid: GridOptions = new Grid()
        .setRememberId('w_email.archive.grid')
        .setLoadDataFunction({ method: 3868 })
        .setKendoConfig({ height: null })
        .setSpecialFunc({
            buttons: [
                {
                    name: 'open_preview',
                    icon: 'far fa-file-search',
                    tooltip: 'open_preview',
                    translateTooltip: true,
                    // @ts-ignore
                    func: (row, button, event) => {
                        if ('contentBytes' in row && !!row['contentBytes'] && !!row['contentType']) {
                            const blobData = atob(row['contentBytes']);
                            let byteNumbers = new Array(blobData.length);

                            for (let i = 0; i < blobData.length; i++) {
                                byteNumbers[i] = blobData.charCodeAt(i);
                            }

                            const byteArray = new Uint8Array(byteNumbers);
                            const fileBlob = new Blob([byteArray], { type: row['contentType'] });
                            const blobUrl = URL.createObjectURL(fileBlob);

                            this.popup.openPopup(blobUrl, { rememberId: 'w_email_archive_file_preview' });
                            // window.open(blobUrl, '_blank', 'popup');
                        }
                    },
                },
            ],
        });

    public onGridReady() {
        if (!this.email) return;

        const attachments = (this.email.attachments ?? []).map((attachment) => ({ ...attachment, id: attachment.id, contentType: attachment.contentType, file_size: attachment.size, filename_name: attachment.name, stockbatch_keynos: '', save_attatchment: attachment.contentType?.includes('image') ? '0' : '1' }));
        this.grid.gridfunc?.setDataSource(attachments);
    }

    constructor(
        private emailService: EmailService,
        private typeahead: TypeaheadService,
        private datatask: DataTaskService,
        private economy: EconomyService,
        private logistic: LogisticService,
        private modal: ModalService,
        private popup: PopupService,
        private projectProcess: ProjectProcessService,
    ) {
        super();
    }

    public async archiveEmail(event: MouseEvent, params?: Partial<{ goto_reply: '1' | '0'; reply_all?: '1' | '0' }>) {
        if (!this.email || this.saving) return;

        this.saving = true;
        try {
            // @ts-ignore
            this.email.attachments = this.grid.gridfunc?.getAllRows() ?? [];

            const email = this.emailService.serializeEmail(this.email);

            const { errorcode, errormessage, email_keyno_new, order_internal_no } = await this.emailService.saveEmailItem(email as EnrichedMessage);

            if (errorcode !== '0') {
                this.modal.show({ title: 'error', translateTitle: true, message: errormessage, type: 'warning' });
                this.email.create_voucher = '0';
            } else {
                this.emailArchived.emit({ email: this.email, email_keyno_new: email_keyno_new, errorcode: errorcode, errormessage: errormessage, goto_reply: params?.goto_reply ?? '0', reply_all: params?.reply_all ?? '0', order_internal_no: order_internal_no, event: event });
            }
        } finally {
            this.saving = false;
        }
    }

    // #region search

    public searchFromCustomer = () => this.emailService.searchAllCustomer(this.email!.from_cust_name);

    public searchCustomer = () => this.emailService.searchAllCustomer(this.email!.cust_name);

    public searchProjects = () => this.emailService.getProjects(this.email!.project_name, this.email!.cust_no);

    public searchCustacts = () => this.typeahead.searchCustacts({ searchtext: this.email!.custact_note, cust_no: this.email?.cust_no, restrict_to_workorder: '0' });

    public searchOrders = () => this.emailService.searchOrders(this.email!.order_search_info, this.email?.cust_no);

    public searchPurchases = () => this.emailService.searchPurchases(this.email!.purch_search_info, this.email?.cust_no);

    // #endregion search

    // #region select

    public onFromCustomerSelected(item?: Item) {
        if (!!this.email) {
            if (!item || this.email.from_cust_no != item?.item_id) {
                this.email.from_cust_no = '0';
                this.email.from_cust_name = '';
                this.fromPersons = [];
                this.email.pers_name = '0';
                this.email.pers_no = '0';
            }

            this.email.from_cust_name = item?.item_name ?? '';
            this.email.from_cust_no = item?.item_id ?? '0';

            if (this.email.from_cust_no > '0') {
                this.loadFromPersons();
            }
            if (this.email.from_cust_no > '0' && this.email.cust_no == '0') {
                this.onCustomerSelected({ item_id: this.email.from_cust_no, item_name: this.email.from_cust_name });
            }
        }
    }

    public onCustomerSelected(item?: Item) {
        if (!!this.email) {
            if (!item || this.email.cust_no != item?.item_id) {
                this.email.cust_no = '0';
                this.email.cust_name = '';
                this.email.project_name = '';
                this.email.project_keyno = '0';

                this.onCustactSelected();
            }

            this.email.cust_name = item?.item_name ?? '';
            this.email.cust_no = item?.item_id ?? '0';

            if (this.email.from_cust_no == '0') {
                this.email.from_cust_no = item?.item_id ?? '0';
                this.email.from_cust_name = item?.item_name ?? '';
                this.loadFromPersons();
            }
        }
    }

    public async onProjectSelected(item?: Item) {
        if (!!this.email) {
            this.email.project_keyno = item?.item_id ?? '0';
            this.email.project_name = item?.item_name ?? '';

            if (this.email.project_keyno > '0') {
                const project = await this.emailService.getProject(this.email.project_keyno);
                this.setProjectInfoToEmail(project);
                this.loadProjectProcesses();
            } else {
                this.projectProcesses = [];
                this.email.projectprosess_keyno = '0';
                this.email.prosess_name = '';
            }
        }
    }

    private setProjectInfoToEmail(project: Project) {
        if (!!this.email) {
            this.email.project_keyno = project?.project_keyno ?? '0';
            this.email.project_name = project?.project_name ?? '';
            this.email.cust_no = project?.cust_no ?? '0';
            this.email.cust_name = project?.cust_name ?? '';
        }
    }

    public onProcessSelected(item?: ProjectProcessItem) {
        if (!!this.email) {
            this.email.projectprosess_keyno = item?.projectprosess_keyno ?? '0';
            this.email.prosess_name = item?.projectprosess_name ?? '0';
        }
    }

    public async onCustactSelected(item?: ItemCustact) {
        if (!!this.email) {
            this.email.custact_no = item?.item_id ?? '0';
            this.email.custact_note = item?.item_name ?? '';

            if (item && this.email.custact_no > '0') {
                const customer = await this.emailService.getCustomerFromCustact(this.email.custact_no);
                this.email = { ...this.email, ...customer };

                this.loadProjectProcesses();
            }
        }
    }

    public onOrderSelected(item?: OrderSearchItem) {
        if (!!this.email) {
            this.email.order_internal_no = item?.order_internal_no ?? '0';
            this.email.order_search_info = item?.item_name ?? '';

            if (this.email.order_internal_no > '0' && this.email.cust_no == '0' && !!item) {
                this.onCustomerSelected({ item_name: item.cust_name, item_id: item.cust_no });
                this.onProjectSelected({ item_name: item.project_name, item_id: item.project_keyno });
            }
        }
    }

    public onPurchaseSelected(item?: PurchSearchItem) {
        if (!!this.email) {
            this.email.purch_internal_no = item?.purch_internal_no ?? '0';
            this.email.purch_search_info = item?.item_name ?? '';

            if (this.email.cust_no == '0' && item?.cust_no_name && item?.cust_no && item?.cust_no != '0') {
                this.onCustomerSelected({ item_name: item.cust_no_name, item_id: item.cust_no });
            }
        }
    }

    // #endregion select

    // #region load

    private async loadFromPersons() {
        if (this.email?.from_cust_no) {
            this.fromPersons = await this.emailService.getCustomerPersons(this.email.from_cust_no);
        }
    }

    private async loadProjectProcesses() {
        if (this.email?.project_keyno) {
            this.projectProcesses = await this.projectProcess.getProjectProcesses({ custact: '0', show_only_timeentry_allowed: '0', project_keyno: this.email.project_keyno });
        }
    }

    private async loadCustactMembers() {
        this.custactMembers = await this.emailService.getCustactMembers();
    }

    private async loadActivityTypes() {
        this.activityTypes = await this.economy.getActivityTypes();
    }

    private async loadCompanies() {
        this.companies = await this.logistic.getCompanies();
    }

    // #endregion load

    public async saveChanges() {
        if (this.saving) return;
        if (!!this.email) {
            try {
                this.saving = true;

                const response = (await this.datatask.Post(3867, this.email))[0];

                if (response?.errorcode !== '0') {
                    this.modal.show({
                        type: 'warning',
                        title: 'error',
                        translateTitle: true,
                        message: response.errormessage,
                    });
                } else {
                }
            } finally {
                this.saving = false;
            }
        }
    }

    ngOnInit(): void {
        this.loadActivityTypes();
        this.loadCustactMembers();
        this.loadCompanies();
        this.loadPortalUsers();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['showCreateVoucher']) {
            this.showCreateVoucher = changes['showCreateVoucher'].currentValue;
        }

        if (!!changes?.['email']?.currentValue && changes['email'].currentValue !== changes['email'].previousValue) {
            if (!!this.email) {
                this.email.activitytype_no = '' + this.email.activitytype_no;
                this.email.portal_user_keyno_responsible = '' + this.email.portal_user_keyno_responsible;
            }

            this.fromPersons = [];
            this.projectProcesses = [];

            if (!!this.email && this.email.from_cust_no > '0') {
                this.loadFromPersons();
            }

            if (!!this.email && this.email.project_keyno > '0') {
                this.loadProjectProcesses();
            }
        }
    }

    public async openCustomerEditPopup() {
        const data = await this.popup.openPopup('customeredit/0', { rememberId: 'emailarchiveform' });

        window.addEventListener('message', this.setDataFromMessage);

        data?.closed.subscribe({
            next: (value) => {
                if (value) {
                    window.removeEventListener('message', this.setDataFromMessage);
                }
            },
        });
    }

    private setDataFromMessage = (event: MessageEvent<any>) => {
        if (Object.hasOwn(event.data, 'type') && event.data.type === 'ttData') {
            const data: { cust_no: string; cust_name: string } = event.data.data;

            if (typeof data === 'object' && 'cust_no' in data && 'cust_name' in data && !!data.cust_no && data.cust_name) {
                this.onFromCustomerSelected({ item_id: data.cust_no, item_name: data.cust_name });
            }

            window.removeEventListener('message', this.setDataFromMessage);
        }
    };
}

// interface EmailArchiveAttachmentRow {
//     id?: string;
//     save_attatchment?: '1' | '0';
//     contentType?: string;
//     file_size?: number;
//     filename_name?: string;
//     stockbatch_keynos?: string;
// }
